/**
 * K78 (c) — antrean persetujuan tarif borongan di atas ambang + ambang per region.
 *
 * Kepala Cabang bebas menyesuaikan tarif region sendiri sampai ambang
 * (`sewing_rate_settings.pct_threshold`, bawaan 10%) naik atau turun dari versi
 * terakhir; di atas itu perubahan DITAHAN menunggu Owner. Kartu ini:
 *   - menampilkan antrean yang menunggu (semua pembaca tarif melihatnya —
 *     Kepala Cabang perlu tahu pengajuannya belum berlaku);
 *   - tombol Setujui/Tolak HANYA untuk Owner (backend menolak peran lain 403;
 *     menyembunyikan tombol bukan keamanan);
 *   - ambang per region; Owner bisa mengubahnya (K78 a: DATA, bukan migration).
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { StatusBadge } from "./StatusBadge";
import Hint from "./Hint";
import { toast } from "sonner";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { formatIDR, formatDate } from "../../lib/format";
import { mintaTeks } from "../../lib/dialogAplikasi";

function pesanGalat(e, cadangan) {
  return apiErrorMessage(e, cadangan);
}

function Persen({ nilai }) {
  if (nilai === null || nilai === undefined) return "—";
  const n = Number(nilai);
  return `${n > 0 ? "+" : ""}${n.toFixed(2).replace(".", ",")}%`;
}

// T111 (2 Nov 2026): `bagian` membelah kartu ini di layar Tarif Borongan — "antrean" (koreksi + menunggu Owner) tampil
// di ATAS, "ambang" di bawah bersama penjelasnya. Tanpa `bagian` = keduanya (bentuk lama).
export default function PersetujuanTarifBorongan({ isOwner, onBerubah, muatUlangKe = 0, bagian = null }) {
  const tampilAntrean = bagian !== "ambang";
  const tampilAmbang = bagian !== "antrean";
  const [menunggu, setMenunggu] = useState([]);
  const [koreksi, setKoreksi] = useState([]);
  const [ambang, setAmbang] = useState([]);
  const [dialog, setDialog] = useState(null); // {aksi: "setujui"|"tolak", item}
  const [catatan, setCatatan] = useState("");
  const [sejak, setSejak] = useState("");
  const [proses, setProses] = useState(false);
  const [ubahAmbang, setUbahAmbang] = useState({});

  const muat = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/master/sewing-rates/perubahan", { params: { status: "PENDING" } }),
        api.get("/master/sewing-rate-settings"),
      ]);
      setMenunggu(p.data.items || []);
      // BUSINESS-19: koreksi tarif beku menunggu Owner (KC/Owner melihat; Owner memutus).
      api.get("/borongan/koreksi-tarif", { params: { status: "PENDING" } })
        .then(({ data }) => setKoreksi(data.items || [])).catch((e) => { setKoreksi([]); toast.error(apiErrorMessage(e, "Antrean koreksi tarif gagal dimuat")); });
      setAmbang(s.data.items || []);
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memuat antrean persetujuan tarif"));
    }
  }, []);

  // `muatUlangKe` dinaikkan induk sesudah pengajuan tarif — pengajuan di atas
  // ambang (HTTP 202) harus langsung tampil di antrean tanpa muat ulang halaman.
  useEffect(() => { muat(); }, [muat, muatUlangKe]);

  const bukaDialog = (aksi, item) => {
    setDialog({ aksi, item });
    setCatatan("");
    setSejak("");
  };

  const putuskan = async () => {
    const { aksi, item } = dialog;
    if (aksi === "tolak" && catatan.trim().length < 5) {
      toast.error("Alasan penolakan wajib diisi (minimal 5 karakter).");
      return;
    }
    setProses(true);
    try {
      const body = { catatan: catatan.trim() || null };
      if (aksi === "setujui" && sejak) body.berlaku_sejak = sejak;
      await api.post(`/master/sewing-rates/perubahan/${item.id}/${aksi}`, body);
      toast.success(aksi === "setujui" ? "Disetujui — versi tarif baru terbit." : "Perubahan ditolak.");
      setDialog(null);
      await muat();
      if (onBerubah) onBerubah();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menyimpan keputusan"));
    } finally {
      setProses(false);
    }
  };

  const putusKoreksi = async (id, setuju) => {
    let catatan = null;
    if (!setuju) {
      catatan = (await mintaTeks("Alasan penolakan (wajib):")) || "";
      if (!catatan.trim()) return;
    }
    try {
      await api.post(`/borongan/koreksi-tarif/${id}/putuskan`, { setuju, catatan });
      toast.success(setuju ? "Koreksi disetujui." : "Koreksi ditolak.");
      muat();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memutus koreksi"));
    }
  };

  const simpanAmbang = async (row) => {
    const nilai = Number(String(ubahAmbang[row.region_id] ?? "").replace(",", "."));
    if (!Number.isFinite(nilai) || nilai < 0 || nilai > 100) {
      toast.error("Ambang harus angka 0–100.");
      return;
    }
    try {
      await api.put(`/master/sewing-rate-settings/${row.region_id}`, { pct_threshold: nilai });
      toast.success(`Ambang ${row.region_name} disimpan.`);
      setUbahAmbang((m) => ({ ...m, [row.region_id]: undefined }));
      muat();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menyimpan ambang"));
    }
  };

  return (
    <div className="space-y-4" data-testid="persetujuan-tarif-borongan">
      {tampilAntrean && koreksi.length > 0 && (
        <div data-testid="antrean-koreksi-tarif">
          <h2 className="text-lg font-medium mb-3">Koreksi Tarif Beku Menunggu Owner</h2>
          <div className="space-y-2">
            {koreksi.map((k) => (
              <Card key={k.id} className="p-4 text-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div>FA <b>{k.fa_number || "—"}</b>: {formatIDR(k.rate_lama_idr)} → <b>{formatIDR(k.rate_benar_idr)}</b></div>
                    <div className="text-muted-foreground">Alasan: {k.alasan}</div>
                    <div className="text-xs text-muted-foreground">Upah yang sudah dibayar tidak diubah; selisih masuk rekap sebagai baris tersendiri.</div>
                  </div>
                  {isOwner && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => putusKoreksi(k.id, true)}>Setujui</Button>
                      <Button size="sm" variant="outline" onClick={() => putusKoreksi(k.id, false)}>Tolak</Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tampilAntrean && menunggu.length > 0 && (
        <div>
          <h2 className="text-lg font-medium mb-3">Menunggu Persetujuan Owner</h2>
          <div className="space-y-2">
            {menunggu.map((it) => (
              <Card key={it.id} className="p-4" data-testid={`perubahan-${it.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1 text-sm">
                    <div className="font-medium">
                      {it.region_name || "—"} · {it.work_type_name || it.work_type_code || "—"}{" "}
                      <StatusBadge tone="warning" className="ml-1">Menunggu Owner</StatusBadge>
                    </div>
                    <div>
                      {formatIDR(it.rate_before_idr)} → <b>{formatIDR(it.rate_after_idr)}</b>{" "}
                      (<Persen nilai={it.pct_change} />) · diminta berlaku {formatDate(it.berlaku_sejak)}
                    </div>
                    <div className="text-muted-foreground">
                      Alasan: {it.reason} · diajukan {it.requested_by_name || "—"}
                    </div>
                  </div>
                  {isOwner && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => bukaDialog("setujui", it)}
                              data-testid={`btn-setujui-${it.id}`}>Setujui</Button>
                      <Button size="sm" variant="outline" onClick={() => bukaDialog("tolak", it)}
                              data-testid={`btn-tolak-${it.id}`}>Tolak</Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tampilAmbang && (
      <Card className="p-4" data-testid="ambang-tarif">
        <h2 className="text-base font-medium">
          Ambang Penyesuaian Kepala Cabang{" "}
          <Hint>Perubahan tarif oleh Kepala Cabang yang melewati ambang ini (naik atau turun) ditahan sampai Owner menyetujui.</Hint>
        </h2>
        <p className="text-sm text-muted-foreground mt-1" data-testid="penjelas-ambang">
          Ini bukan tarif: ini batas seberapa jauh Kepala Cabang boleh mengubah tarif wilayahnya sendiri. Perubahan
          yang lebih besar dari batas ini menunggu persetujuan Owner.
        </p>
        <div className="mt-3 space-y-2">
          {ambang.map((row) => (
            <div key={row.region_id} className="flex flex-wrap items-center gap-3 text-sm">
              <span className="w-28">{row.region_name || "—"}</span>
              <span className="font-medium w-16">{Number(row.pct_threshold).toString().replace(".", ",")}%</span>
              {isOwner && (
                <>
                  <Input className="w-24 h-8" inputMode="decimal" placeholder="baru"
                         value={ubahAmbang[row.region_id] ?? ""}
                         onChange={(e) => setUbahAmbang((m) => ({ ...m, [row.region_id]: e.target.value }))}
                         data-testid={`input-ambang-${row.region_id}`} />
                  <Button size="sm" variant="outline" disabled={!ubahAmbang[row.region_id]}
                          onClick={() => simpanAmbang(row)}>Simpan</Button>
                </>
              )}
            </div>
          ))}
          {ambang.length === 0 && (
            <p className="text-sm text-muted-foreground">Belum ada ambang untuk cabang Anda.</p>
          )}
        </div>
      </Card>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog?.aksi === "setujui" ? "Setujui perubahan tarif" : "Tolak perubahan tarif"}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3 text-sm">
              <p>
                {dialog.item.region_name} · {dialog.item.work_type_name || dialog.item.work_type_code}:{" "}
                {formatIDR(dialog.item.rate_before_idr)} → {formatIDR(dialog.item.rate_after_idr)}
              </p>
              {dialog.aksi === "setujui" && (
                <div>
                  <Label htmlFor="sejak-setujui">Berlaku sejak (opsional)</Label>
                  <Input id="sejak-setujui" type="date" value={sejak} onChange={(e) => setSejak(e.target.value)} />
                  <p className="text-xs text-muted-foreground mt-1">
                    Kosong = tanggal yang diminta, atau hari ini bila tanggal itu sudah lewat.
                  </p>
                </div>
              )}
              <div>
                <Label htmlFor="catatan-keputusan">
                  {dialog.aksi === "tolak" ? "Alasan penolakan (wajib)" : "Catatan (opsional)"}
                </Label>
                <Textarea id="catatan-keputusan" value={catatan} onChange={(e) => setCatatan(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={proses}>Batal</Button>
            <Button onClick={putuskan} disabled={proses} data-testid="btn-putuskan">
              {dialog?.aksi === "setujui" ? "Setujui" : "Tolak"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
